import { useAuthStore } from '@/stores/auth';

export const fetchWrapper = {
  get: request('GET'),
  post: request('POST'),
  put: request('PUT'),
  delete: request('DELETE'),
  patch: request('PATCH')
};

interface RequestOptions {
  method: string;
  headers: Record<string, string>;
  body?: string;
  credentials: RequestCredentials;
}

interface ApiResponse<T> {
  status: string;
  message?: string;
  data: T | null;
}

function request(method: string) {
  return async <T>(url: string, body?: object, options?: { headers?: Record<string, string> }): Promise<ApiResponse<T>> => {
    const authStore = useAuthStore();
    const csrfToken = authStore.getCsrfToken();

    const requestOptions: RequestOptions = {
      method,
      headers: {
        ...authHeader(url),
        ...(options?.headers || {})
      },
      credentials: 'include'  // Include cookies in requests
    };
    
    if (body) {
      requestOptions.headers['Content-Type'] = 'application/json';
      requestOptions.body = JSON.stringify(body);
    }

    // Add CSRF token for API requests
    if (url.startsWith(import.meta.env.VITE_API_URL) && csrfToken) {
      requestOptions.headers['X-CSRFToken'] = csrfToken;
    }
    
    return fetch(url, requestOptions).then(handleResponse<T>);
  };
}

// helper functions

function authHeader(url: string): Record<string, string> {
  const { user } = useAuthStore();
  const isLoggedIn = !!user;
  const isApiUrl = url.startsWith(import.meta.env.VITE_API_URL);
  
  const headers: Record<string, string> = {};
  
  if (isLoggedIn && isApiUrl) {
    // Add any additional auth headers here if needed
  }
  
  return headers;
}

async function handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
  const text = await response.text();
  
  const data = text && JSON.parse(text);

  if (!response.ok) {
    const { user, logout } = useAuthStore();
    if ([401, 403].includes(response.status) && user) {
      // auto logout if 401 Unauthorized or 403 Forbidden response returned from api
      logout();
    }

    const error = (data && data.message) || response.statusText;
    return Promise.reject(error);
  }

  return data as ApiResponse<T>;
}
